// frontend/src/components/common/PlayerStatsTable.tsx
// The shared player season-stats board: column config + mobile bucket-paging +
// the thin ResponsiveStatsTable adapter. Extracted from PublicPlayerStatsPage
// (2026-07-09) so the pre-draft research board renders the SAME table instead
// of a drifting copy. `extraColumnsAfterId` lets draft surfaces prepend e.g. a
// Draft Rank column after the pinned Player/Pos/Team columns; the public
// /stats page passes nothing (draft rank is a different use case - owner call).
import React from 'react';

import { PlayerRead } from '@/api/schemas';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';
import { positionTextClasses } from '@/utils/positionColors';
import InjuryBadge from '@/components/common/InjuryBadge';
import ResponsiveStatsTable, { StatColumn, MobileGroup } from '@/components/common/ResponsiveStatsTable';

export const formatStat = (value: number | null | undefined, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value.toFixed(decimals);
};
export const num = (decimals: number) => (v: unknown): string =>
  formatStat(typeof v === 'number' ? v : (v == null ? null : Number(v)), decimals);

// Raw season-stats rows come back loosely typed; normalize to PlayerRead-ish.
export const transformStatsToPlayerRead = (statsEntry: any): PlayerRead => ({
  ...statsEntry,
  name: statsEntry.player_name || statsEntry.name,
  primary_position: statsEntry.fantasy_position || statsEntry.fantasy_position_in_league,
  fantasy_position_in_league: statsEntry.fantasy_position || statsEntry.fantasy_position_in_league,
});

// Columns. `groups` drives the mobile bucket-paging: Player/Pos/Team ('id') stay
// pinned in every tab; each stat column lists the tab(s) it appears in (its own
// bucket + the "All" superset). Physical order below = desktop order = "All" tab
// order. Desktop shows all columns regardless of group.
export const PLAYER_COLUMNS: StatColumn<PlayerRead>[] = [
  {
    key: 'name', header: 'Player', group: 'id', frozen: true, sortable: true, minWidthPx: 110,
    cellClassName: 'hover:text-accent-yellow hover:underline hover:underline-offset-2',
    cell: (p) => (
      // Principle: the table is for STATS — the name should never crowd them out.
      // Mobile caps the column at 40% of the screen (40vw minus the cell's 1rem
      // padding, so the whole column lands at ~40vw); short names use less, giving
      // the rest back to the scrolling stats. A long name truncates with an
      // ellipsis — the full name is one tap away (row opens the player). Desktop
      // (sm+) caps at 13rem. vw/px max-width is used deliberately: table-layout:
      // auto ignores max-width on cells but honors it on an inline element. The
      // injury badge sits outside the truncation so it's never clipped.
      <span className="inline-flex items-center gap-1.5 max-w-[calc(40vw-1rem)] sm:max-w-[13rem] align-middle" title={formatPlayerDisplayName(p)}>
        <span className="truncate min-w-0">{formatPlayerDisplayName(p)}</span>
        <InjuryBadge status={p.injury_status} details={p.injury_details} className="flex-none" />
      </span>
    ),
  },
  { key: 'primary_position', header: 'Pos', align: 'center', group: 'id', sortable: true, accessor: (p) => p.primary_position, format: (v) => (v as string) ?? '-' },
  {
    key: 'current_team_name', header: 'Team', align: 'center', group: 'id', sortable: true,
    accessor: (p) => p.current_team_name,
    // Native title tooltip = the full club name on hover (owner ask
    // 2026-07-20: La Liga abbreviations are unfamiliar - "DEP"?). Native
    // rather than the styled header tooltip: data cells sit at every depth
    // of the scroll container, where a positioned tooltip clips at the
    // edges; title renders outside the clip box at any row.
    // No cursor-help: the ?-cursor read as "something's wrong" (owner
    // 2026-07-20) - the plain hover tooltip is the whole affordance.
    cell: (p) => (
      <span title={p.current_team_name ?? undefined}>
        {getAbbreviation(p.current_team_name)}
      </span>
    ),
  },

  { key: 'fantasy_points', header: 'PTS', tooltip: 'Total Fantasy Points', align: 'right', groups: ['ranks', 'all'], sortable: true, cellClassName: 'font-bold', accessor: (p) => (p as any).fantasy_points, format: num(1) },
  { key: 'fppg', header: 'FPPG', tooltip: 'Fantasy Points Per Game', align: 'right', groups: ['ranks', 'all'], sortable: true, cellClassName: 'font-bold', accessor: (p) => (p as any).fppg, format: num(1) },
  { key: 'player_games_appeared', header: 'MP', tooltip: 'Matches Played', align: 'right', groups: ['all'], sortable: true, accessor: (p) => (p as any).player_games_appeared, format: num(0) },
  { key: 'rank', header: 'Rank', tooltip: 'Rank', align: 'right', groups: ['ranks', 'all'], sortable: true, accessor: (p) => (p as any).rank, format: num(0) },
  { key: 'position_rank', header: 'PosR', tooltip: 'Position Rank', align: 'right', groups: ['ranks', 'all'], sortable: true, accessor: (p) => (p as any).position_rank, format: num(0) },

  { key: 'goals', header: 'GL', tooltip: 'Goals', align: 'right', groups: ['scoring', 'all'], sortable: true, accessor: (p) => (p as any).goals, format: num(0) },
  { key: 'shots_on_target', header: 'SoT', tooltip: 'Shots on Target', align: 'right', groups: ['scoring', 'all'], sortable: true, accessor: (p) => (p as any).shots_on_target, format: num(0) },
  { key: 'shots', header: 'SHT', tooltip: 'Shots', align: 'right', groups: ['scoring', 'all'], sortable: true, accessor: (p) => (p as any).shots, format: num(0) },

  { key: 'gca', header: 'GC', tooltip: 'Goal-Creating Actions', align: 'right', groups: ['creating', 'all'], sortable: true, accessor: (p) => (p as any).gca, format: num(0) },
  { key: 'sca', header: 'SC', tooltip: 'Score-Creating Actions', align: 'right', groups: ['creating', 'all'], sortable: true, accessor: (p) => (p as any).sca, format: num(0) },

  { key: 'prog_passes', header: 'IP', tooltip: 'Impact Passes', align: 'right', groups: ['progression', 'all'], sortable: true, accessor: (p) => (p as any).prog_passes, format: num(0) },
  { key: 'prog_carries', header: 'IC', tooltip: 'Impact Carries', align: 'right', groups: ['progression', 'all'], sortable: true, accessor: (p) => (p as any).prog_carries, format: num(0) },
  { key: 'dispossessed', header: 'DIS', tooltip: 'Dispossessions', align: 'right', groups: ['progression', 'all'], sortable: true, accessor: (p) => (p as any).dispossessed, format: num(0) },

  // Set-piece duty counts (owner 2026-07-20): discovery columns - sort by COR
  // to find the set-piece takers; the modal chips carry the curated %-verdict.
  { key: 'corners_taken', header: 'COR', tooltip: 'Corners Taken', align: 'right', groups: ['all'], sortable: true, accessor: (p) => (p as any).corners_taken, format: num(0) },
  { key: 'free_kicks_taken', header: 'FK', tooltip: 'Free Kicks Taken (final third)', align: 'right', groups: ['all'], sortable: true, accessor: (p) => (p as any).free_kicks_taken, format: num(0) },
  { key: 'penalties_taken', header: 'PK', tooltip: 'Penalties Taken', align: 'right', groups: ['all'], sortable: true, accessor: (p) => (p as any).penalties_taken, format: num(0) },
];

export const PLAYER_MOBILE_GROUPS: MobileGroup[] = [
  { key: 'ranks', label: 'Ranks' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'creating', label: 'Creating' },
  { key: 'progression', label: 'Progression' },
  { key: 'all', label: 'All' },
];

// Thin adapter: maps the page's props onto the shared ResponsiveStatsTable with
// the player column config + mobile bucket-paging.
export interface PlayerStatsTableProps {
  players: PlayerRead[];
  isLoading: boolean;
  error: string | null;
  onPlayerRowClick?: (player: PlayerRead) => void;
  sortConfig?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  onSortChange?: (key: string) => void;
  /** Extra columns spliced in AFTER the pinned Player/Pos/Team columns -
   *  e.g. the pre-draft board's Draft Rank. Omit for the public /stats page. */
  extraColumnsAfterId?: StatColumn<PlayerRead>[];
  /** Extra columns inserted before the identity columns. */
  extraColumnsBeforeId?: StatColumn<PlayerRead>[];
  /** Persistent row actions appended after every stat column. */
  extraColumnsAtEnd?: StatColumn<PlayerRead>[];
  /** Draft-only mobile treatment: combine position + club under Player. */
  compactMobileIdentity?: boolean;
  mobileGroups?: MobileGroup[];
  mobileGroupVariant?: 'pills' | 'tabs';
  mobileGroupLeading?: React.ReactNode;
  virtualRowHeightPx?: number;
}

const PlayerStatsTable: React.FC<PlayerStatsTableProps> = ({
  players,
  isLoading,
  error,
  onPlayerRowClick,
  sortConfig,
  onSortChange,
  extraColumnsAfterId,
  extraColumnsBeforeId,
  extraColumnsAtEnd,
  compactMobileIdentity = false,
  mobileGroups = PLAYER_MOBILE_GROUPS,
  mobileGroupVariant = 'pills',
  mobileGroupLeading,
  virtualRowHeightPx,
}) => {
  // Splice extra columns in right after the pinned id-group columns. Derive
  // the boundary from the group rather than hardcoding "3" so adding/removing
  // a pinned column can't misplace the extra column.
  const columns = React.useMemo(() => {
    const compactIdentityOffsetPx = (extraColumnsBeforeId ?? []).reduce(
      (total, column) => total + (column.widthPx ?? 0),
      0,
    );
    const baseColumns = compactMobileIdentity
      ? PLAYER_COLUMNS.map(column => {
          if (column.key === 'name') {
            return {
              ...column,
              widthPx: 128,
              minWidthPx: 128,
              leftOffsetPx: compactIdentityOffsetPx,
              cellClassName: '',
              cell: (player: PlayerRead) => {
                const position = player.primary_position ?? '—';
                return (
                  <span className="flex min-w-0 flex-col leading-tight" title={formatPlayerDisplayName(player)}>
                    <span className="flex min-w-0 items-center gap-1">
                      <span className="truncate font-bold text-gray-100">{formatPlayerDisplayName(player)}</span>
                      <InjuryBadge status={player.injury_status} details={player.injury_details} className="flex-none" />
                    </span>
                    <span className="mt-0.5 truncate text-[10px] font-normal text-gray-500">
                      <strong className={positionTextClasses(position)}>{position}</strong>
                      {' · '}{getAbbreviation(player.current_team_name)}
                    </span>
                  </span>
                );
              },
            } satisfies StatColumn<PlayerRead>;
          }
          if (column.key === 'primary_position' || column.key === 'current_team_name') {
            return { ...column, group: 'desktop-only', groups: ['desktop-only'] };
          }
          return column;
        })
      : PLAYER_COLUMNS;
    if (!extraColumnsBeforeId?.length && !extraColumnsAfterId?.length && !extraColumnsAtEnd?.length) return baseColumns;
    const firstStat = baseColumns.findIndex(c => c.group !== 'id' && c.group !== 'desktop-only');
    const splitAt = firstStat === -1 ? baseColumns.length : firstStat;
    return [
      ...(extraColumnsBeforeId ?? []),
      ...baseColumns.slice(0, splitAt),
      ...(extraColumnsAfterId ?? []),
      ...baseColumns.slice(splitAt),
      ...(extraColumnsAtEnd ?? []),
    ];
  }, [compactMobileIdentity, extraColumnsBeforeId, extraColumnsAfterId, extraColumnsAtEnd]);
  if (error) return <p className="text-center text-secondary p-4">{error}</p>;
  return (
    <ResponsiveStatsTable<PlayerRead>
      columns={columns}
      rows={players}
      rowKey={(p) => p.id}
      sortBy={sortConfig?.sortBy}
      sortOrder={sortConfig?.sortOrder}
      onSortChange={onSortChange}
      onRowClick={onPlayerRowClick}
      isLoading={isLoading}
      emptyMessage="No players match your filters."
      mobileGroups={mobileGroups}
      mobileGroupVariant={mobileGroupVariant}
      mobileGroupLeading={mobileGroupLeading}
      idGroup="id"
      virtualize
      virtualRowHeightPx={virtualRowHeightPx}
      ariaLabel="Player statistics - pick a stat group or scroll to see more"
    />
  );
};

export default PlayerStatsTable;
